import React, { useMemo } from "react";
import { Film, Sparkles } from "lucide-react";

/**
 * CinemaTheater
 *
 * Wraps the live broadcast in a movie-theater proscenium frame:
 *  - Velvet red curtains (open when content is playing, closed during intermission)
 *  - Gilded marquee header with blinking bulbs
 *  - Auditorium walls + projector beams + audience silhouettes + aisle lights
 *  - Screen frame with warm gold trim + projector bloom
 *
 * Pure CSS / SVG. No new dependencies.
 *
 * Props:
 *   - active: boolean — when false, render `children` as-is (theater mode OFF)
 *   - curtainsOpen: boolean — open during live playback, closed during intermission/paywall/off-air
 *   - marqueeTitle: string — what the marquee displays (e.g. "Now Showing · GCR Action")
 *   - marqueeSub: string  — small sub-text under the marquee title (e.g. show name)
 *   - intermissionLabel: string — text rendered between the closed curtains (e.g. "INTERMISSION")
 *   - children: the actual video / paywall / off-air content
 */
export default function CinemaTheater({
  active = true,
  curtainsOpen = true,
  marqueeTitle = "Now Showing",
  marqueeSub = "",
  intermissionLabel = "INTERMISSION",
  intermissionContent = null,
  children,
}) {
  // 24 marquee bulbs across the top edge
  const bulbs = useMemo(() => Array.from({ length: 24 }), []);

  if (!active) {
    return <>{children}</>;
  }

  return (
    <div className="theater-room absolute inset-0 overflow-hidden" data-testid="cinema-theater">
      {/* Projector beams (subtle, decorative) */}
      <div className="theater-beam left" />
      <div className="theater-beam right" />

      {/* Marquee header */}
      <div className="theater-marquee absolute top-0 left-0 right-0 z-10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Film className="w-5 h-5 text-[#f6c34a] shrink-0" />
          <div className="min-w-0">
            <div
              data-testid="theater-marquee-title"
              className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#f6c34a] font-bold"
            >
              {marqueeTitle}
            </div>
            {marqueeSub && (
              <div
                data-testid="theater-marquee-sub"
                className="text-sm sm:text-base font-bold text-white truncate"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {marqueeSub}
              </div>
            )}
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#f6c34a]/70">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Cinema mode</span>
        </div>

        {/* Row of blinking bulbs along the bottom edge of the marquee */}
        <div className="absolute left-0 right-0 -bottom-[6px] flex justify-around px-4 pointer-events-none">
          {bulbs.map((_, i) => (
            <span key={i} className="theater-marquee-bulb" />
          ))}
        </div>
      </div>

      {/* Stage / screen area — centered, framed */}
      <div className="absolute inset-0 flex items-center justify-center pt-14 pb-[14vh] px-4 sm:px-12">
        <div
          className="theater-screen-frame relative w-full max-w-[1280px] aspect-video"
          data-testid="theater-screen"
        >
          {/* The actual streamed content (video / paywall / off-air card) renders here */}
          <div className="absolute inset-0 overflow-hidden rounded-[3px]">
            {children}
          </div>

          {/* Subtle screen flicker overlay, only when curtains are open */}
          {curtainsOpen && <div className="theater-screen-flicker" aria-hidden="true" />}

          {/* Intermission label — only visible when curtains are closed */}
          {!curtainsOpen && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[6] px-6">
              {intermissionContent ? (
                <div className="pointer-events-auto w-full max-w-md">{intermissionContent}</div>
              ) : (
                <div
                  data-testid="theater-intermission-label"
                  className="theater-intermission text-3xl sm:text-5xl"
                >
                  {intermissionLabel}
                </div>
              )}
            </div>
          )}

          {/* Velvet curtains (left & right) — sit OUTSIDE the inner overflow, OVER the screen */}
          <div
            data-testid="theater-curtain-left"
            className={`theater-curtain left ${curtainsOpen ? "open" : "closed"}`}
            aria-hidden="true"
          />
          <div
            data-testid="theater-curtain-right"
            className={`theater-curtain right ${curtainsOpen ? "open" : "closed"}`}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Audience silhouettes (rows of seat backs) */}
      <div className="theater-seats" aria-hidden="true" />

      {/* Aisle floor LED strip */}
      <div className="theater-aisle-lights" aria-hidden="true" />
    </div>
  );
}
